import { FC } from "react";
import { useTranslation } from "react-i18next";
import { FormikHelpers, useFormik } from "formik";
import * as Yup from "yup";
import { StringInput } from "~/components";
import {
  AutocompleteInput,
  AutocompleteOption,
  FormContainer,
  LaurenQuoteMobile,
  NextButton,
  Prompt,
  QuestionContainer,
  QuestionPageContainer,
  RadioInput,
} from "~/OnboardingApp/components";
import { useOnboarding } from "~/OnboardingApp/hooks/useOnboarding";
import { useSubmitOnboardingQuestion } from "~/OnboardingApp/hooks/useSubmitOnboardingQuestion";
import { usePartnersIndexQuery } from "~/store/api";
import { requiredString } from "~/utils/validations";

export type CurrentClinic = "" | "Yes" | "No";

type Values = {
  currentClinic: CurrentClinic;
  currentPartnerId: number | null;
  currentProvider: string;
};

const values: CurrentClinic[] = ["Yes", "No"];

const validationSchema = Yup.object({
  currentClinic: requiredString.oneOf(values),
  currentPartnerId: Yup.number()
    .nullable()
    .when("currentClinic", {
      is: (value: CurrentClinic) => value === "Yes",
      then: (schema) => schema.required(),
    }),
  currentProvider: Yup.string(),
});

const queryArgs = { type: "inactive" as const };

export const CurrentClinicPage: FC = () => {
  const { t } = useTranslation();
  const { onSubmit } = useSubmitOnboardingQuestion();
  const { currentClinic, currentPartnerId, currentProvider } = useOnboarding();
  const { data: partnersData, isLoading: partnersLoading } =
    usePartnersIndexQuery(queryArgs);

  const initialValues: Values = {
    currentClinic: currentClinic || "",
    currentPartnerId: currentPartnerId ?? null,
    currentProvider: currentProvider || "",
  };

  // When the user is not working with a clinic, clear both follow-up answers so a
  // previously selected clinic/physician does not linger.
  const handleSubmit = (vals: Values, helpers: FormikHelpers<Values>) => {
    const payload =
      vals.currentClinic === "No"
        ? { currentClinic: "No" as const, currentPartnerId: null, currentProvider: "" }
        : {
            currentClinic: vals.currentClinic,
            currentPartnerId: vals.currentPartnerId || null,
            currentProvider: vals.currentProvider,
          };

    return onSubmit(payload, helpers);
  };

  const formik = useFormik({
    initialValues,
    onSubmit: handleSubmit,
    validationSchema,
    validateOnMount: true,
  });

  const showAdditionalQuestions = formik.values.currentClinic === "Yes";
  const isAutoAdvance = (option: string) => option === "No";
  const commentCopy = t("onboarding.currentClinicPage.comment");

  const clinicOptions: AutocompleteOption[] =
    partnersData?.data?.map((partner) => ({
      value: Number(partner.id),
      label: partner.attributes.nameWithLocation,
    })) || [];

  // Show the button once the user has answered No, or selected a clinic after Yes.
  // The physician field is optional and intentionally does not gate the button.
  const showButton =
    formik.values.currentClinic === "No" ||
    (showAdditionalQuestions && !!formik.values.currentPartnerId);

  return (
    <QuestionPageContainer>
      <FormContainer formik={formik}>
        <QuestionContainer>
          <Prompt
            comment={commentCopy}
            question={t("onboarding.currentClinicPage.question")}
          />
          {commentCopy && (
            <LaurenQuoteMobile initialShow>{commentCopy}</LaurenQuoteMobile>
          )}

          <RadioInput
            autoAdvance={isAutoAdvance}
            id="currentClinic"
            values={values}
            defaultValue={currentClinic}
          />
        </QuestionContainer>

        {showAdditionalQuestions && (
          <>
            <QuestionContainer>
              <Prompt
                description={
                  <em>
                    {t(
                      "onboarding.currentClinicPage.clinicQuestion.instruction",
                    )}
                  </em>
                }
                question={t(
                  "onboarding.currentClinicPage.clinicQuestion.question",
                )}
              />
              <AutocompleteInput
                id="currentPartnerId"
                isDisabled={partnersLoading}
                options={clinicOptions}
                placeholder={t(
                  "onboarding.currentClinicPage.clinicQuestion.placeholder",
                )}
              />
            </QuestionContainer>

            <QuestionContainer>
              <Prompt
                question={t(
                  "onboarding.currentClinicPage.physicianQuestion.question",
                )}
              />
              <StringInput
                id="currentProvider"
                maxLength={255}
                placeholder={t(
                  "onboarding.currentClinicPage.physicianQuestion.placeholder",
                )}
              />
            </QuestionContainer>
          </>
        )}

        <NextButton showButton={showButton} />
      </FormContainer>
    </QuestionPageContainer>
  );
};
